import type { CustomerContact } from '@clockwise/clients/customer-contacts';
import { formatPhone } from '@clockwise/shared';
import { Pencil } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { CONTACT_TYPE_LABELS, contactName } from '@/lib/customer-contact-display';
import { useHasPermission } from '@/lib/permissions';
import { AddressLines } from './address-lines';
import { DetailRow } from './detail-list';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contact: CustomerContact | null;
  /** Invoked when the viewer clicks Edit; the parent swaps in the form sheet. */
  onEdit?: () => void;
}

/**
 * Read-only detail for a single customer contact, opened by clicking a row in
 * the contacts tab. The Edit action is gated on `customer.contact.edit`.
 */
export function CustomerContactViewSheet({ open, onOpenChange, contact, onEdit }: Props) {
  const canEdit = useHasPermission('customer.contact.edit');

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="flex flex-col gap-0 sm:max-w-lg">
        <SheetHeader>
          <SheetTitle>Contact</SheetTitle>
          <SheetDescription>Contact details for this customer.</SheetDescription>
        </SheetHeader>

        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
          {contact ? (
            <dl className="grid grid-cols-1 sm:grid-cols-[140px_1fr]">
              <DetailRow label="Name">
                <span className="font-medium">{contactName(contact)}</span>
                {contact.primary ? (
                  <span className="ml-2 rounded bg-emerald-100 px-1.5 py-0.5 text-xs font-medium text-emerald-900 dark:bg-emerald-900/30 dark:text-emerald-200">
                    Primary
                  </span>
                ) : null}
              </DetailRow>
              <DetailRow label="Title">{contact.title || '—'}</DetailRow>
              <DetailRow label="Email">
                {contact.email ? (
                  <a
                    className="underline-offset-2 hover:underline"
                    href={`mailto:${contact.email}`}
                  >
                    {contact.email}
                  </a>
                ) : (
                  '—'
                )}
              </DetailRow>
              <DetailRow label="CC emails">
                {contact.ccEmails.length > 0 ? (
                  // A contact can carry up to 50 CC addresses; bound the height
                  // so a long list scrolls instead of dominating the sheet.
                  <div className="max-h-24 space-y-0.5 overflow-y-auto break-words">
                    {contact.ccEmails.map((e) => (
                      <div key={e}>{e}</div>
                    ))}
                  </div>
                ) : (
                  '—'
                )}
              </DetailRow>
              <DetailRow label="Work phone">{formatPhone(contact.workPhone) || '—'}</DetailRow>
              <DetailRow label="Mobile">{formatPhone(contact.mobile) || '—'}</DetailRow>
              <DetailRow label="Type">
                {contact.contactType ? CONTACT_TYPE_LABELS[contact.contactType] : '—'}
              </DetailRow>
              <DetailRow label="Address">
                <AddressLines
                  line1={contact.address1}
                  line2={contact.address2}
                  locality={contact.locality}
                  region={contact.region}
                  postalCode={contact.postalCode}
                  country={contact.country}
                />
              </DetailRow>
              <DetailRow label="Created">{new Date(contact.createdAt).toLocaleString()}</DetailRow>
              <DetailRow label="Updated">{new Date(contact.updatedAt).toLocaleString()}</DetailRow>
            </dl>
          ) : null}
        </div>

        <SheetFooter className="flex-row justify-end border-t">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
          {canEdit && onEdit ? (
            <Button type="button" onClick={onEdit}>
              <Pencil />
              Edit
            </Button>
          ) : null}
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
